/**
 * updateChecker — non-intrusive "a new version shipped" toast.
 *
 * How it works:
 *   1. On boot, fetch `/build.json` and remember the buildId we
 *      started with.
 *   2. Every `intervalMs` (default 3 minutes) AND whenever the tab
 *      regains focus, fetch `/build.json` again with a cache-bust
 *      param so the CDN doesn't serve us the old one.
 *   3. If the polled buildId differs from the booted one, fire one
 *      Sonner toast with a "Refresh" action. The toast persists
 *      (no auto-dismiss) so the user can finish what they're doing
 *      and click refresh on their own terms.
 *
 * Why a static JSON file:
 *   - It's emitted by a Vite plugin at build time (see vite.config.ts
 *     `buildInfoPlugin`) so we don't need a runtime endpoint.
 *   - Vercel re-deploys = new `build.json` at the same URL.
 *   - In local dev there's no /build.json — the fetch 404s, the
 *     checker stays idle, no toast. Clean no-op in dev.
 *
 * Pair: `src/components/shared/ErrorBoundary.tsx` handles the case
 * where the user takes the chunk-load hit BEFORE noticing the toast.
 */

import { toast } from "sonner";

interface BuildInfo {
  buildId: string;
  builtAt?: string;
}

let bootedBuildId: string | null = null;
let toastShown = false;

async function fetchBuildInfo(): Promise<BuildInfo | null> {
  try {
    // Cache-bust on every poll. Without `?t=...` the CDN happily
    // hands us the previously-cached build.json forever.
    const res = await fetch(`/build.json?t=${Date.now()}`, {
      cache: "no-store",
      // Don't credentials-include — build.json is public, and we
      // don't want this poll to count against rate-limited auth flows.
      credentials: "omit",
    });
    if (!res.ok) return null;
    return (await res.json()) as BuildInfo;
  } catch {
    return null;
  }
}

function showUpdateToast() {
  if (toastShown) return;
  toastShown = true;
  toast.message("An update is available", {
    description: "Refresh to apply the latest version.",
    // Infinity = stays until the user interacts with it. The update
    // isn't urgent enough to interrupt — we just want it parked in
    // the corner until they're at a stopping point.
    duration: Infinity,
    action: {
      label: "Refresh",
      onClick: () => {
        window.location.reload();
      },
    },
  });
}

async function checkOnce() {
  if (toastShown || !bootedBuildId) return;
  const info = await fetchBuildInfo();
  if (!info?.buildId) return;
  if (info.buildId !== bootedBuildId) {
    showUpdateToast();
  }
}

/**
 * Start polling for new deploys. Returns a cleanup function that
 * stops the timer + removes the focus listener — wire this up via
 * useEffect so it tears down cleanly on hot reload in dev.
 *
 * Safe to call before/after authentication — the poll is anonymous
 * and the file is public.
 */
export function startUpdateChecker(intervalMs: number = 3 * 60 * 1000): () => void {
  let pollTimer: number | null = null;
  let cancelled = false;

  // Capture the booted buildId. If we can't reach build.json on
  // first try, no big deal — leave bootedBuildId null and the
  // checks below will short-circuit, so we never falsely prompt.
  void fetchBuildInfo().then((info) => {
    if (cancelled) return;
    if (info?.buildId) {
      bootedBuildId = info.buildId;
      // Kick off the regular poll only once we have a baseline.
      pollTimer = window.setInterval(() => {
        void checkOnce();
      }, intervalMs);
    }
  });

  // Focus is usually the freshest signal — a user who left the tab
  // open for hours then returned is exactly the case we want to
  // catch quickly.
  const onFocus = () => {
    void checkOnce();
  };
  window.addEventListener("focus", onFocus);

  return () => {
    cancelled = true;
    if (pollTimer !== null) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
    window.removeEventListener("focus", onFocus);
  };
}
